/** @jsx jsx */

import { jsx } from '@emotion/core';

import React, { useState, Fragment } from 'react';
import { Box, Button, Typography, Divider, FormHelperText } from '@material-ui/core';
import Modal from '../../../../pge-plus-common/Modal';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import colors from '../../../../../themes/main-colors';
import { styled } from '@material-ui/core/styles';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { Question } from '../../../../pge-plus-common/Typography';
import theme from '../../../../../themes/theme';
import useFormState from '../../../../../hooks/useFormState';
import TextField from '../../../../text-field';
import { RouteComponentProps } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import { EVPulseSmartChargingEnrollment, UpdateSectionErrorCode } from '../rebateOnlyForm.types';
import { evPulseEnrollmentValidateFunction } from '../rebateOnlyForm.rules';
import { navigate } from 'gatsby';
import FileUploaderWithOCR from '../../../../pge-plus-common/FileUploaderWithOCR';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import { ApplicationErSectionType, ApplicationSectionStatus, ErEnrollmentPreference, OcrFieldNames, SectionErRebateInfo} from '../../../../../__generated__/pgeplus-types';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateInfoERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
import { hasVinInputChanged } from '../../../../pge-plus-common/utils/chargerVehicleQualification';

const PREFIX = 'evPulse';
const classes = {
  submit: `${PREFIX}-submit`,
  errorMessage: `${PREFIX}-errorMessage`,
  imageTitle: `${PREFIX}-imageTitle`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled(Box)(({ theme: theme2 }) => ({
  [`& .${classes.submit}`]: {
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    [theme2.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  [`& .${classes.errorMessage}`]: {
    fontFamily: 'Untitled-Sans',
    fontSize: '1rem !important',
  },
  [`& .${classes.imageTitle}`]: {
    fontSize: '16px !important',
    padding: '0px !important',
    color: `${colors.sparkBlue} !important`,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'unset !important',
    },
  },
}));

const UploadVIN: React.FC<RouteComponentProps> = () => {
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_EV_PULSE_ENTROLLMENT,
  );
  const { content: evPulseEligibleContent } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_ELIGIBLE_FOR_EVPLUS,
  );
  const { content: fileUploaderText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS,
  );
  const [ocrExtractionStatus, setOcrExtractionStatus] = useState<
    string | undefined
  >();
  const [openExamplePhotoModal, setOpenExamplePhotoModal] = useState(false);

  const { wrapWithLoader } = useWrapWithLoader();
  const {
    formState: { rebateInfo, rebateConfirm, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const form = useFormState(rebateInfo.evPulse!, {
    validate: evPulseEnrollmentValidateFunction(),
    validationContext: {
      content,
    },
  });
  const { updateSectionRebateInfo } = useUpdateERSections();
  const sectionDataRebateInfo: SectionErRebateInfo = getSectionData(
    ApplicationErSectionType.ErRebateInfo,
  );
  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(2),
  };
  const boldText = {
    fontWeight: 700,
  };
  
  const { setErrorNotification } = useUtil();

  const chargerIsQualified = !!rebateInfo?.chargerValidation?.isQualifiedProduct;
  const vehicleIsQualified = !!rebateInfo?.vehicleValidation?.isQualifiedVehicle;

  const onSubmit = wrapWithLoader(
    async (data: EVPulseSmartChargingEnrollment) => {
      try {
        const response = await updateSectionRebateInfo(
          rebateInfoERFormStateAdapter().fromFormState(
            {
              ...rebateInfo,
              evPulse: { ...data },
              qualifiedProductInfo: rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyCharger
                ? rebateInfo.qualifiedProductInfo
                : undefined,
              enrollmentPreference: hasVinInputChanged(data, rebateInfo.evPulse)
                ? undefined
                : rebateInfo.enrollmentPreference,
            },
            application?.id!,
            sectionDataRebateInfo?.id,
            ApplicationSectionStatus.Completed,
          ),
        );

        // Check for errors to show the red banner and block navigation
        if (
          response?.errors ||
          response.data?.updateSectionERRebateInfo?.validation?.errors
        ) {
          throw new Error();
        }

        //Store the data in global store
        if (response.data?.updateSectionERRebateInfo?.section.data) {
          const updatedValues = rebateInfoERFormStateAdapter().toFormState(
            response.data?.updateSectionERRebateInfo?.section.data,
          );
          updateFormState('rebateInfo', {
            ...updatedValues,
          });
        }

        if (!response.data?.updateSectionERRebateInfo.section.errors.length) {
          if (
            chargerIsQualified &&
            vehicleIsQualified &&
            rebateInfo.enrollmentPreference !== ErEnrollmentPreference.UseMyVehicle
          ) {
            // Note: Both qualified > charger-path > user connecting their vehicle app .. already did Customer Info, skip ahead to Eligibility Confirmation
            void navigate(routes.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION, {
              state: {
                message: 'EV_PULSE_REBATE_OFFER_DETAILS',
                backButtonRoute: routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN,
                // CUP-24447: Now points at the new "Let's Connect" instructions screen instead of the legacy Connect Tesla confirmation screen
                nextButtonRoute: routes.PGE_PLUS_REBATE_ONLY_VEHICLE_INSTRUCTIONS,
              },
            });
          } else {
            // Note : Only vehicle is qualified user establishing qualification for the first time.. continue into the full flow
            void navigate(routes.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION);
          }
        } else {
          const ineligibleErrors = response.data?.updateSectionERRebateInfo.section.errors.filter(
              reason =>
                reason.code === UpdateSectionErrorCode.VINAlreadyEnrolled,
            );

          if (ineligibleErrors.length) {
            const messageState = { message: 'REBATE_ONLY_SMART_CHARGING_INELIGIBLE' };

            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: messageState,
            });
            return;
          } else {
            throw new Error();
          }
        }
      } catch (e) {
        setErrorNotification(true);
      }
    },
  );

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateInfo({
        ...rebateInfoERFormStateAdapter().fromFormState(
          {
            ...rebateInfo,
            evPulse: { ...form.values },
            qualifiedProductInfo: rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyCharger
              ? rebateInfo.qualifiedProductInfo
              : undefined,
            enrollmentPreference: hasVinInputChanged(form.values, rebateInfo.evPulse)
              ? undefined
              : rebateInfo.enrollmentPreference,
          },
          application?.id!,
          sectionDataRebateInfo?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateInfo?.validation?.errors ||
        response.data?.updateSectionERRebateInfo?.section?.errors.length !== 0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });
  
  const defaultBackRoute = () => {
    if (
      sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments &&
      sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments?.evPulse &&
      sectionDataRebateInfo?.data?.rebateInfo?.existingEnrollments?.evPulse
        ?.length > 0 &&
      !rebateInfo.evPulseEnrollmentConfirmation?.willEnrollNewSmartChargingDevice &&
      rebateInfo.evPulseEnrollmentConfirmation?.willEnrollNewVehicle
    ) {
      return routes.PGE_PLUS_REBATE_ONLY_VERIFY_EVPULSE_REBATE;
    }

    return routes.PGE_PLUS_REBATE_ONLY_UPLOAD_MODEL_NUMBER;
  };
  const backRoute = () => {
    if (chargerIsQualified && vehicleIsQualified) {
      // Both qualified + vehicle flow: back to enrollment choice
      if (rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyVehicle) {
        return routes.PGE_PLUS_REBATE_ONLY_ENROLL_WITH_CHARGER_OR_VEHICLE;
      }
      // Both qualified + charger flow: came from ConnectChargerToWifi
      return routes.PGE_PLUS_REBATE_ONLY_CONNECT_CHARGER;
    }
    if (!chargerIsQualified && vehicleIsQualified) {
      return routes.PGE_PLUS_REBATE_ONLY_UPLOAD_MODEL_NUMBER;
    }
    return defaultBackRoute();
  };

  return (
    <Root>
      <form
        onSubmit={form.submit(values => onSubmit(values))}
        noValidate
      >
        <Fragment>
          <Box mt={3}>
            <Question
              title={content.get('REBATE_ONLY_TAKE_PHOTO_TESLA_VIN_HEADER')}
                  detail={content.get(
                    'REBATE_ONLY_WHERE_TO_FIND_TESLA_VIN_DETAILS',
                  )}
            />
            <Box mt={2}>
              {!form.values?.vinImage && (
                <Box mt={3}>
                  <Button
                    className={classes.imageTitle}
                    onClick={() => setOpenExamplePhotoModal(true)}
                  >
                    {evPulseEligibleContent.get('EVPULSE_ENROLLMENT_VIN_SAMPLE_LINK_TEXT')}
                  </Button>
                  <Modal
                    open={openExamplePhotoModal}
                    onClose={() => setOpenExamplePhotoModal(false)}
                    title={evPulseEligibleContent.get('MODEL_SAMPLE_PHOTO_TITLE')}
                    showCloseIcon={true}
                    modalStyles={{ width: 600 }}
                  >
                    <Box mt={3}>
                      <img
                        css={{
                          width: '100%',
                          maxHeight: '350px',
                          objectFit: 'cover',
                        }}
                        src={evPulseEligibleContent.get('SAMPLE_IMAGE_URL')}
                      />
                    </Box>
                  </Modal>
                </Box>
              )}
              <FileUploaderWithOCR
                image={form.values?.vinImage}
                placeholder={fileUploaderText.get('UPLOAD_A_PHOTO')}
                reuploadLabel={fileUploaderText.get('REUPLOAD_PHOTO')!}
                ocrType={OcrFieldNames.Vin}
                onComplete={(key, extractedText, message, status) => {
                  void form.setValue('vinImage', { id: key });
                  void form.setValue('vin', extractedText);
                  setOcrExtractionStatus(status);
                }}
              />
              {form.errors.vinImage && (
                <FormHelperText className={classes.errorMessage} error>
                  {form.errors.vinImage}
                </FormHelperText>
              )}
              {form.values?.vinImage && (
                    <Box
                      css={{
                        marginBottom: theme.spacing(1),
                        '& .MuiFormControl-root': {
                          background: 'transparent',
                        },
                        '& .MuiInputBase-input': {
                          background: '#FFF',
                        },
                      }}
                    >
                  {ocrExtractionStatus && ocrExtractionStatus === 'fail' && (
                    <FormHelperText
                      className={classes.errorMessage}
                      error
                      component="div"
                    >
                      {fileUploaderText.get('OCR_FAIL_MESSAGE')}
                    </FormHelperText>
                  )}
                  <Typography css={{ ...subText1, ...boldText }}>
                        {ocrExtractionStatus &&
                        ocrExtractionStatus === 'success'
                      ? content.get('REBATE_ONLY_EDIT_VIN_HEADER')
                      : content.get('VIN')}
                  </Typography>
                  <TextField
                        css={{
                          background: 'transparent',
                          '.MuiInputBase-formControl': {
                            background: colors.white,
                          },
                          width: '100%',
                        }}
                    name={'vin'}
                    {...form.props('vin')}
                  />

                    {ocrExtractionStatus &&
                      ocrExtractionStatus === 'success' && (
                    <Typography css={{ marginTop: 8 }}>
                      {fileUploaderText.get('VIN_SUCCESS_MESSAGE')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Divider css={{ color: colors.gray93, margin: '30px 0 30px 0' }}  />
          </Box>
        </Fragment>

      <ProceedOrCancelButton
        back={backRoute()}
        disable={!form.values?.vin}
      />

      </form>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </Root>
  );
};

export default UploadVIN;
